import { ChevronDownIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  HEADER_PILL,
  HEADER_PILL_ACTIVE,
  HEADER_PILL_IDLE,
} from '@/shared/components/pillClasses';
import { cn } from '@/lib/utils';

import { PAGE_COPY } from '../copy';
import {
  DATE_RANGES,
  RANGE_LABEL,
  STATE_LABEL,
  STATES,
  type NotificationsSearch,
} from '../pageFilter';
import { TYPE_GROUPS, typeGroupLabel, type TypeGroup } from '../typeGroups';

/** A select's "no filter" entry; never a workspace id, which is a UUID. */
const ANY = 'any';

export interface WorkspaceOption {
  id: string;
  name: string;
}

function pill(active: boolean): string {
  return cn(
    HEADER_PILL,
    active ? HEADER_PILL_ACTIVE : HEADER_PILL_IDLE,
    // The select's own field look, undone: a filter here is a pill.
    'w-fit max-w-56 justify-start [&_svg]:size-3 [&_svg]:text-current',
  );
}

/**
 * The filters beside the status tabs (spec 147 FR-002): Workspace, Type,
 * Date (with From and To under Custom) and State. Each pill names its filter
 * until it is set, then what it is set to.
 */
export function PageFilters({
  search,
  workspaces,
  onChange,
}: {
  search: NotificationsSearch;
  workspaces: readonly WorkspaceOption[];
  onChange: (next: NotificationsSearch) => void;
}) {
  const workspaceName = workspaces.find(
    (workspace) => workspace.id === search.workspace,
  )?.name;
  const types = search.type ?? [];
  const range = search.range ?? 'all';

  return (
    <>
      <Select
        value={search.workspace ?? ANY}
        onValueChange={(next) => {
          onChange({
            ...search,
            workspace: next && next !== ANY ? next : undefined,
          });
        }}
      >
        <SelectTrigger
          aria-label={PAGE_COPY.workspace}
          className={pill(search.workspace !== undefined)}
        >
          <SelectValue>
            {() => (
              <span className="truncate">
                {search.workspace
                  ? (workspaceName ?? PAGE_COPY.workspace)
                  : PAGE_COPY.workspace}
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72 overflow-y-auto">
          <SelectItem value={ANY}>{PAGE_COPY.allWorkspaces}</SelectItem>
          {workspaces.map((workspace) => (
            <SelectItem key={workspace.id} value={workspace.id}>
              {workspace.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={PAGE_COPY.type}
          className={pill(types.length > 0)}
        >
          <span className="truncate">
            {types.length === 0
              ? PAGE_COPY.type
              : types.length === 1 && types[0]
                ? typeGroupLabel(types[0])
                : `${PAGE_COPY.type} · ${String(types.length)}`}
          </span>
          <ChevronDownIcon aria-hidden="true" className="size-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-56">
          {TYPE_GROUPS.map((group) => (
            <DropdownMenuCheckboxItem
              key={group.id}
              checked={types.includes(group.id)}
              closeOnClick={false}
              onCheckedChange={(checked: boolean) => {
                const next: TypeGroup[] = checked
                  ? [...types, group.id]
                  : types.filter((id) => id !== group.id);
                onChange({
                  ...search,
                  // In menu order, as the address reads them back.
                  type:
                    next.length > 0
                      ? TYPE_GROUPS.map((g) => g.id).filter((id) =>
                          next.includes(id),
                        )
                      : undefined,
                });
              }}
            >
              {group.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Select
        value={range}
        onValueChange={(next) => {
          const picked = DATE_RANGES.find((value) => value === next);
          onChange({
            ...search,
            range: picked && picked !== 'all' ? picked : undefined,
            from: picked === 'custom' ? search.from : undefined,
            to: picked === 'custom' ? search.to : undefined,
          });
        }}
      >
        <SelectTrigger
          aria-label={PAGE_COPY.date}
          className={pill(range !== 'all')}
        >
          <SelectValue>
            {() => (
              <span className="truncate">
                {range === 'all' ? PAGE_COPY.date : RANGE_LABEL[range]}
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {DATE_RANGES.map((value) => (
            <SelectItem key={value} value={value}>
              {RANGE_LABEL[value]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {range === 'custom' ? (
        // On a phone (in the filters sheet) From and To share a full-width
        // row, as 44px fields at 16px, the size below which iOS zooms the
        // page on focus (spec 274).
        <span className="flex flex-wrap items-center gap-2 max-md:w-full max-md:flex-nowrap">
          <label className="text-muted-foreground flex items-center gap-1.5 text-xs max-md:min-w-0 max-md:flex-1">
            {PAGE_COPY.from}
            <Input
              type="date"
              value={search.from ?? ''}
              max={search.to}
              onChange={(event) => {
                onChange({ ...search, from: event.target.value || undefined });
              }}
              className="h-7 w-auto text-xs max-md:h-11 max-md:min-w-0 max-md:flex-1 max-md:text-base md:text-xs"
            />
          </label>
          <label className="text-muted-foreground flex items-center gap-1.5 text-xs max-md:min-w-0 max-md:flex-1">
            {PAGE_COPY.to}
            <Input
              type="date"
              value={search.to ?? ''}
              min={search.from}
              onChange={(event) => {
                onChange({ ...search, to: event.target.value || undefined });
              }}
              className="h-7 w-auto text-xs max-md:h-11 max-md:min-w-0 max-md:flex-1 max-md:text-base md:text-xs"
            />
          </label>
        </span>
      ) : null}

      <Select
        value={search.state ?? ANY}
        onValueChange={(next) => {
          onChange({
            ...search,
            state: STATES.find((value) => value === next),
          });
        }}
      >
        <SelectTrigger
          aria-label={PAGE_COPY.state}
          className={pill(search.state !== undefined)}
        >
          <SelectValue>
            {() => (
              <span className="truncate">
                {search.state ? STATE_LABEL[search.state] : PAGE_COPY.state}
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>All</SelectItem>
          {STATES.map((value) => (
            <SelectItem key={value} value={value}>
              {STATE_LABEL[value]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
}
